import { useState } from "react";
import { Check } from "lucide-react";
import { formatPrice } from "@/lib/store";
import { TRADE_CATEGORIES, tradeModels, DEDUCTIONS, NO_STORAGE } from "@/lib/tradeCatalog";
import { ModelPicker } from "@/components/site/ModelPicker";
import { chip } from "@/components/site/OrderSpecFields";
import { pInput } from "./ItemRows";

const Tick = ({ on, onClick, label, amount, testid, plus }) => (
  <button type="button" role="checkbox" aria-checked={on} data-testid={testid} onClick={onClick}
    className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition-colors ${on ? "bg-white/10" : "hover:bg-white/[0.06]"}`}>
    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-white bg-white text-black" : "border-white/30"}`}>{on && <Check className="h-3 w-3" strokeWidth={3} />}</span>
    <span className="flex-1 text-zinc-200">{label}</span>
    <span className={plus ? "text-emerald-300" : "text-zinc-400"}>{plus ? "+" : "−"} {formatPrice(amount)}</span>
  </button>
);

// Calcula o aparelho da troca pela tabela "Valores da troca" (modelo/GB menos os descontos marcados).
export const TradeTablePicker = ({ prices, bonus, onAdd }) => {
  const [category, setCategory] = useState("iphone");
  const [model, setModel] = useState("");
  const [storage, setStorage] = useState("");
  const [hits, setHits] = useState([]);
  const [rc, setRc] = useState(false);
  const priced = new Set(prices.map((p) => p.model));
  const models = tradeModels(category).filter((m) => priced.has(m.name));
  const doc = prices.find((p) => p.model === model);
  const storages = Object.keys(doc?.values || {});
  const ded = Object.entries(doc?.deductions || {}).filter(([, v]) => v > 0);
  const base = doc?.values?.[storage] || 0;
  const value = Math.max(0, base - ded.filter(([k]) => hits.includes(k)).reduce((s, [, v]) => s + v, 0)) + (rc ? bonus : 0);
  const choose = (name) => { setModel(name); setStorage(Object.keys(prices.find((p) => p.model === name)?.values || {})[0] || ""); setHits([]); };
  const toggle = (k) => setHits((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));
  const label = (k) => DEDUCTIONS.find(([id]) => id === k)?.[1] || k;
  const add = () => onAdd({ label: [model, storage !== NO_STORAGE ? storage : null].filter(Boolean).join(" "), value });

  return (
    <div data-testid="proposal-trade-table" className="mt-3 rounded-2xl border border-white/10 p-3">
      <div className="flex flex-wrap gap-2">
        {TRADE_CATEGORIES.map((c) => <button type="button" key={c.id} data-testid={`proposal-table-category-${c.id}`} onClick={() => { setCategory(c.id); setModel(""); }} className={chip(category === c.id)}>{c.label}</button>)}
      </div>
      {models.length ? (
        <ModelPicker testid="proposal-table-model" value={model} onChange={choose} options={models.map((m) => m.name)} selectOnly groupOf={(o) => models.find((m) => m.name === o)?.group} placeholder="Toque para escolher o aparelho" className={`${pInput} mt-3`} />
      ) : <p className="mt-3 text-sm text-zinc-400">Nenhum valor de troca cadastrado nessa categoria.</p>}
      {doc && (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {storages.map((s) => <button type="button" key={s} data-testid={`proposal-table-storage-${s}`} onClick={() => setStorage(s)} className={chip(storage === s)}>{s === NO_STORAGE ? "Único" : s} · {formatPrice(doc.values[s])}</button>)}
          </div>
          {(ded.length > 0 || bonus > 0) && (
            <div className="mt-3 space-y-0.5">
              {ded.map(([k, v]) => <Tick key={k} on={hits.includes(k)} onClick={() => toggle(k)} label={label(k)} amount={v} testid={`proposal-table-ded-${k}`} />)}
              {bonus > 0 && <Tick on={rc} onClick={() => setRc((v) => !v)} label="Comprado na RC iPhones (bônus)" amount={bonus} plus testid="proposal-table-bonus" />}
            </div>
          )}
          <button type="button" data-testid="proposal-table-add" disabled={!base} onClick={add} className="mt-3 w-full rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-[1.01] disabled:opacity-40">
            Usar na troca · {formatPrice(value)}
          </button>
        </>
      )}
    </div>
  );
};
